import type { ToolItem, ToolType } from './types';

export const GenerateNowText = 'Generate Now';

export const TOOL_INFO: Record<ToolType, ToolItem> = {
  'ai-video-generator': {
    title: 'AI Video Generator from Text & Image',
    description:
      'Create stunning AI videos from text or images in seconds. Simply type your prompt or upload an image with a description, and watch your ideas come to life. Whether you prefer realistic visuals or animated styles, your vision is just a few clicks away!',
    features: [
      {
        title: 'Easy to Use for Everyone',
        description:
          'Generate AI videos instantly with one click. Vidu AI makes it quick and easy to turn your ideas into videos.',
      },
      {
        title: 'Diverse AI Video Templates',
        description:
          'Provide various AI video templates to create AI hugging and AI kissing videos without any prompt.',
      },
      {
        title: 'Adjustable AI Video Settings',
        description:
          'Tailor your AI video with duration, movement amplitude, and resolution settings to meet your needs.',
      },
    ],
    generates: [
      {
        title: 'Text to Video with AI',
        description:
          "With Vidu's free AI text-to-video generator, quickly transform your text into videos. Whether a movie script or a short paragraph, Vidu enables you to rapidly generate high-quality AI videos using advanced AI technology. Enjoy fast generation in seconds and 1080p resolution, allowing you to unleash creativity and easily create videos.",
      },
      {
        title: 'Image to Video Using AI',
        description:
          "Upload your images, and Vidu's AI image-to-video generator will instantly transform them into high-quality, AI-generated videos. Customize the start and last frames to perfect your AI video! With both realistic and animated options, Vidu delivers fast and accurate video creation that brings your ideas to life.",
      },
      {
        title: 'Multi-Subject Consistency for AI Video Generation',
        description:
          'Vidu AI provides multi-subject consistency, allowing you to generate AI videos from multiple images while ensuring the characters and scenes remain consistent. This feature allows for easy management of various characters or objects, maintaining high levels of stability and accuracy in your videos.',
      },
      {
        title: 'Generate Realistic & Animated AI Videos',
        description:
          "Whether you prefer realistic or animated styles, Vidu's AI video creator makes choosing and creating videos that fit your desired aesthetic easy. Tailor your creations with either style to capture every frame with detailed precision and enhance your visual storytelling.",
      },
    ],
    steps: {
      title: 'How to Create AI Videos with Vidu?',
      list: [
        {
          title: 'Enter Prompt or Upload Image(s)',
          description:
            'Choose Reference to Video, Image to Video, or Text to Video. Enter your text description or upload your photo(s) along with a description.',
        },
        {
          title: 'Customize Your AI Video',
          description:
            'Choose the length of time, adjust the resolution for optimal quality, and set the movement amplitude to control the intensity of motion.',
        },
        {
          title: 'Generate and Download',
          description:
            'Click the Create button and wait a moment for the video to generate. Once ready, download the video by clicking the button at the bottom right corner.',
        },
      ],
    },
    faqs: {
      title: 'FAQs About AI Video Generator',
      list: [
        {
          question: 'What is the best AI Video Generator with a human avatar?',
          answer:
            'Vidu is a top choice for AI video creation with human avatars. It features advanced capabilities like reference-to-video and animated styles, ensuring engaging and realistic results.',
        },
        {
          question: "Is Vidu's AI Video Creator free to use?",
          answer:
            'Yes, Vidu AI video generation tool is free to use. Each user receives 40 free credits per month. This allows all users to experience the Vidu AI video generator at no cost.',
        },
        {
          question: 'What is the best AI Video Generator?',
          answer:
            'Vidu is one of the best AI video creators, powered by cutting-edge text/image-to-video technology. Beyond basic AI video generation, it offers exceptional subject consistency and animation styles. ',
        },
        {
          question: 'How does Vidu AI Video Generator work?',
          answer:
            'Vidu AI video generator is powered by the Vidu model. It excels in semantic accuracy, visual aesthetics, and multi-entity consistency. Users can choose 4-second or 8-second durations and start quickly. Vidu supports various camera techniques, dynamic motion, and popular features like reference-to-video, animated styles, and image-to-video. It delivers high-quality visuals with fast generation, producing a 4-second video in just seconds.',
        },
      ],
    },
    generateNow: {
      title: 'Vidu AI Video Generator',
    },
  },
  'ai-animation-generator': {
    title: 'AI Animation Generator',
    description:
      "Turn your text into AI animations or bring your artwork to life with Vidu's free AI animation generator. It's fast, easy, and delivers high-quality animated videos.",
    features: [
      {
        title: 'Fast Generation Speed',
        description: 'Say goodbye to long wait times and experience the efficiency of AI-powered animation.',
      },
      {
        title: 'Seamless AI Animation',
        description:
          'Vidu AI animation generator ensures seamless transitions and lifelike movement throughout your videos.',
      },
      {
        title: 'Generate Anything in Animation',
        description:
          'From anime characters to scenery, our AI animation generator brings all your creative ideas to life.',
      },
    ],
    generates: [
      {
        title: 'Text to Animation Video with One Click',
        description:
          "Generate animations from text without any skills. With Vidu's AI text-to-animation generator, you can easily convert text or artwork into animated videos, allowing you to create high-quality animation videos in seconds.",
      },
      {
        title: 'Generate Multiple Animations at Once',
        description:
          'Create up to four unique animation variations with a single click, letting you compare different effects to find the perfect one. Save time and easily experiment with multiple options!',
      },
      {
        title: 'Create AI Animation Videos for Social Media',
        description:
          "Easily generate AI animation videos tailored for social media. Whether you're sharing on Instagram, RedNote, or YouTube, Vidu allows you to customize the video size to fit any platform, with aspect ratios of 9:16, 16:9, and 1:1.",
      },
      {
        title: 'Control Movement Amplitude of Animation',
        description:
          'Control the movement amplitude to create subtle or bold animations, tailoring the motion of your characters and objects to your desired effect.',
      },
    ],
    steps: {
      title: 'How to Make Animated Videos',
      list: [
        {
          title: 'Choose the Feature You Need',
          description:
            'Choose Text to Video or Image to Video to generate animations from text or images. Or select Reference to Video to merge elements from different artworks into one.',
        },
        {
          title: 'Upload Artwork or Enter Text',
          description:
            "Upload your images—whether it's multiple angles of a character, characters, scenery, or objects, and describe your ideas or type your text prompt for animation.",
        },
        {
          title: 'Choose Animation Settings and Create',
          description:
            'For Text to Video, select Animation, set the duration, and aspect ratio, then click Create. For Text/Image to Video, choose animation settings and click Create.',
        },
      ],
    },
    faqs: {
      title: 'FAQs About AI Animation Generator',
      list: [
        {
          question: 'What is the best free AI animation generator?',
          answer:
            'Vidu AI animation generator is one of the best free options for creating high-quality AI animation videos. It allows users to easily generate animations from text or images and offers advanced features like merging characters, objects, and backgrounds. Vidu also ensures fast generation speed, making it a highly efficient tool for anyone looking to create animations quickly.',
        },
        {
          question: 'Can anyone make an animated movie?',
          answer:
            'Yes, anyone can make an animated movie with Vidu AI animation generator. Powered with cutting-edge AI image/text-to-animation ability, Vidu can generate animated characters, scenery, and anything in animation.',
        },
        {
          question: 'What is the best AI for cartoon animation?',
          answer:
            'Vidu stands out for producing high-quality cartoon animations with fast generation speed. You can create cartoon animations from text or images for free online in just three easy steps. With Vidu, users can create cohesive cartoon animations, ensuring consistent characters and scenery throughout the video.',
        },
      ],
    },

    generateNow: {
      title: 'Vidu AI Animation Generator',
    },
  },

  'ai-image-animator': {
    title: 'Animate Photos with AI Image Animator',
    description:
      "Bring life to your still photos with Vidu's AI image animation maker. Animate old photos, portraits, sketches, and paintings to create dynamic, eye-catching videos in seconds.",
    features: [
      {
        title: 'Animate Images with High-Resolution',
        description:
          'Turn static images into high-quality, dynamic animations while maintaining crisp, detailed resolution.',
      },
      {
        title: 'Various AI Animation Templates',
        description:
          'Choose from templates like kiss, hug, and AI outfits to easily animate your images with just a few clicks.',
      },
      {
        title: 'Lifelike Movements & Expressions',
        description:
          'Enjoy seamless animations with fluid, natural transitions, bringing your images to life with realistic motion.',
      },
    ],
    generates: [
      {
        title: 'Animate Any Photo into Videos with AI',
        description:
          "Vidu's AI image animator brings your images to life, whether old photos, anime, paintings, sketches, or portraits. Bring old photos to life, animate your favorite anime characters, or make elements in a painting come alive, turning static artwork into dynamic, interactive visuals.",
      },
      {
        title: 'Animate Every Element in Your Images',
        description:
          'With Vidu, you can change every element of your images. Change character movements, add realistic actions to objects, make backgrounds shift seamlessly, or even alter the weather. Whether a subtle motion or a dramatic transformation, Vidu gives you the creative freedom to bring your vision to life in stunning detail.',
      },
      {
        title: 'Customize Start and Last Frames of Animation',
        description:
          "Vidu's Image to Video feature allows you to perfect your animation by setting both your video's start and last frames. Vidu AI ensures smooth transitions based on your prompts, saving time and giving you full control over your animation’s key moments for the best visual impact.",
      },
      {
        title: 'Ensure Consistency in Multi-Angle Characters & Objects',
        description:
          "Upload multiple angles of a character or object and provide your prompt to animate them while maintaining consistency. Vidu's Reference to Video feature ensures your character or object appears unchanged from all perspectives, preserving its integrity throughout the video.",
      },
    ],
    steps: {
      title: 'How to Animate a Picture?',
      list: [
        {
          title: 'Upload Your Images',
          description: 'Select either Reference to Video or Image to Video, and upload your images.',
        },
        {
          title: 'Enter a Text Prompt to Animate',
          description: 'Provide a text description of how you want the image to animate.',
        },
        {
          title: 'Adjust Settings and Create',
          description:
            'Choose your preferred settings, such as duration, resolution, and more, then click the "Create" button to generate your animation.',
        },
      ],
    },
    faqs: {
      title: 'FAQs About AI Image Animator',
      list: [
        {
          question: 'How can I make photo animation online for free?',
          answer:
            "You can make photo animations online for free with Vidu's AI image animation maker. Choose Image to Video, upload your images, and provide a prompt describing how you want the image to move. Once ready, click the Create button to generate your animated video.",
        },
        {
          question: 'What is the best free AI image animator?',
          answer:
            "Vidu's AI image animator is a top choice for animating images. It offers high-quality, dynamic animations with lifelike movements and expressions. The platform also provides a wide range of animation templates, such as kiss, hug, and AI outfits, allowing for easy animation creation with just a few clicks.",
        },
        {
          question: 'Is Vidu AI Image Animator free to use?',
          answer: 'Yes, Vidu AI Image Animator is free to use. Every user receives 40 free credits each month.',
        },
        {
          question: 'Is there an AI to animate pictures?',
          answer:
            "Yes, Vidu provides an AI-powered image animator to bring your pictures to life. Whether old photos, anime, portraits, or sketches, Vidu's AI can animate these images, creating dynamic videos with smooth, realistic movements. It supports animating faces, portraits, objects, and entire scenes with fluid transitions.",
        },
      ],
    },
    generateNow: {
      title: 'Animate Photos with Vidu AI Image Animator',
    },
  },
  'ai-ad-generator': {
    title: 'AI Video Ad Generator',
    description:
      'Grow your business with our powerful AI tool—generate high-quality video ads in seconds. Create and scale marketing videos to boost sales. Try it now!',
    features: [
      {
        title: 'Instant AI Video Ads Generation',
        description:
          'Create AI-powered video ads instantly with minimal waiting time. Experience the power of rapid video production without delays.',
      },
      {
        title: 'Generate Multiple AI Video Ads at Once',
        description:
          'Generate multiple versions of your AI ad videos in one go. Pick the one that best suits your campaign and save time on edits.',
      },
      {
        title: 'AI Video Ads in 1080p Resolution',
        description:
          'Get stunning AI-generated video ads in full 1080p resolution. Enjoy sharp, high-quality visuals that make your ads stand out.',
      },
    ],
    generates: [
      {
        title: 'Turn Still Product Images into Moving Video Ads',
        description:
          'Effortlessly bring your product images to life with our Image-to-Video feature. Create engaging promotional videos with smooth animations and stunning effects that captivate your audience and highlight your product in seconds.',
      },
      {
        title: 'Customize Backdrops for Promo Videos',
        description:
          "Easily swap out your product's backdrop to match any theme or setting. From a chic studio ambiance to a bustling marketplace, personalized backdrops ensure your promo videos resonate with your brand's vision and stand out in style.",
      },
      {
        title: 'Create Different Looks in One Click for Video Ads',
        description:
          'Harness the power of AI to change outfits, accessories, shoes instantly, and more on any model. You can create diverse looks with just one click, making your promotional content versatile and tailored for different markets and audiences.',
      },
      {
        title: 'Precision Control Over Camera Motion',
        description:
          'Elevate your ad videos with precise control over camera movements. Use detailed prompts to adjust angles, zooms, and motion, enabling you to craft professional, cinematic shots that enhance the appeal of every frame.',
      },
    ],
    steps: {
      title: 'How to Create an Ad Video?',
      list: [
        {
          title: 'Upload Product Images',
          description: 'Choose Reference to Video or Image to Video and upload your product images.',
        },
        {
          title: 'Enter Text Prompt',
          description:
            "Describe how you want the camera to showcase your product or specify the type of backdrop you'd like.",
        },
        {
          title: 'Create and Download Ad Video',
          description:
            'Set the duration, movement amplitude, resolution, and quantity, then create and download your ad video.',
        },
      ],
    },
    faqs: {
      title: 'FAQs About AI Ad Generator',
      list: [
        {
          question: 'Is Vidu AI video ad generator free to use?',
          answer:
            "Yes, Vidu AI's video ad generator is free to use. It allows users to create promotional videos from text and images, offering a seamless and accessible platform for generating engaging ad video content. However, some premium features or advanced customization options require a paid plan.",
        },
        {
          question: 'What is the best free AI ad generator?',
          answer:
            'Vidu is one of the top choices for creating videos for advertisements. It lets you turn static product images into dynamic, captivating videos, effortlessly change backgrounds for more engaging visuals, and provides a free version available to all users.',
        },
        {
          question: 'Is there an AI social media ad generator?',
          answer:
            'Yes, Vidu AI video ad generator is a top choice. It creates engaging ad videos, allows background swaps, and lets you customize dimensions like 9:16, 16:9, and 1:1 for different platforms.',
        },
      ],
    },
    generateNow: {
      title: 'AI Video Ad Generator',
    },
  },
  'ai-hug-generator': {
    title: 'AI Hug Video Generator',
    description:
      'Create heartwarming hug videos online for free with ease. Use templates to instantly generate hugs featuring celebrities, anime characters, loved ones, and more, all with amazing results.',
    features: [
      {
        title: 'Quick Hug Video Creation',
        description:
          'Create a heartwarming hugging video in no time! With Vidu, you can animate your photos into touching hug scenes in seconds.',
      },
      {
        title: 'Pre-Made Hug Video Template',
        description:
          "Save time with Vidu's AI-powered hug video template. Simply apply the template to your images and create hugging videos instantly—no prompts required.",
      },
      {
        title: 'Free AI Tool for Hug Video Animation',
        description:
          'Transform your photos into hug videos for free. Vidu allows you to easily create hugging animations at no cost.',
      },
    ],
    generates: [
      {
        title: 'Make Two People or Characters Hug in Your Photos',
        description:
          "Bring your photos to life with Vidu's advanced AI, which animates two people embracing. Whether it's you hugging a loved one, a favorite anime character, a celebrity, or anyone you choose, you can also bring any person or character you love to life in a warm embrace.",
      },
      {
        title: 'One-Click Hug Video Creation',
        description:
          'Vidu offers a one-click solution to simplify animation. Users can upload their image and within seconds get an animated hug video ready for use. It eliminates the need to enter prompts or other settings, being ideal for those who want to effortlessly create hug videos.',
      },
      {
        title: 'Realistic and Emotional Hug Videos',
        description:
          'Our hug videos are realistic and emotional, which can bring the uploaded images to life with warm and sincere embraces. The movements and facial expressions are smooth and natural without distortion. The appearance of characters remains true to the original image during the embrace, creating deeply emotional and lifelike moments.',
      },
      {
        title: 'Hugging Animations Between Any Creatures',
        description:
          "Our AI Hug Generator is capable of creating more than just human-to-human hugs. It can generate heartwarming moments such as a loving embrace with a pet or a cute interaction between a cat and a dog. Whether it's between humans and animals or among animals, the AI-powered hugs can bring connection and affection to life in various touching scenes.",
      },
    ],
    steps: {
      title: 'How to create AI hug videos?',
      list: [
        {
          title: 'Choose the AI Hug Template',
          description: 'Select the second template—Hug Template.',
        },
        {
          title: 'Upload Your Image',
          description: 'Upload a single image with two people or two individual images of the same aspect ratio.',
        },
        {
          title: 'Create and Download the AI Hug Video',
          description: 'Click the Create button to generate your AI hug video, then download it.',
        },
      ],
    },
    faqs: {
      title: 'FAQs About AI Hug Generator',
      list: [
        {
          question: 'What is the best AI hug generator?',
          answer:
            "Vidu is one of the best AI hug generators. It doesn't require any input prompts or video settings. You simply upload an image, and Vidu generates a hug video. It's also free for all users.",
        },
        {
          question: 'Which AI can make people hug?',
          answer:
            'There are several AI platforms that can generate hugging videos, and Vidu AI is one of the best options. Vidu offers an AI hug video template for all users, allowing you to generate hugging videos with just one click.',
        },
        {
          question: 'How to do AI hug free?',
          answer:
            'To make AI hug videos free, you can use the free Vidu AI hug generator. Simply select the Hug template, upload an image with a character, and click Create.',
        },
        {
          question: 'How can I download the AI hug video?',
          answer:
            'After generating the hug video, hover your mouse over the video. You will see a download button in the bottom-right corner. Click this button to download the hug video.',
        },
      ],
    },
    generateNow: {
      title: 'AI Hug Video Generator',
    },
  },
  'ai-kissing-generator': {
    title: 'AI Kissing Generator',
    description:
      'Create lifelike and romantic kissing videos with Vidu AI Kissing Generator. Upload two images of loved ones or characters, and let our AI transform them into realistic, heartwarming kissing moments.',
    features: [
      {
        title: 'Completely Free & Simple',
        description:
          'Create stunning kissing videos for free with a platform designed to be intuitive and easy to use.',
      },
      {
        title: 'AI-Powered Kissing Templates',
        description:
          'Get started instantly with ready-made AI templates that allow you to generate realistic kissing animations with ease.',
      },
      {
        title: 'Total Privacy and Security Guaranteed',
        description:
          'Your personal data is fully protected, ensuring a secure and private experience whenever you create it.',
      },
    ],
    generates: [
      {
        title: 'Create a Kissing Video in Seconds',
        description:
          'With AI-powered technology, you can transform your favorite characters or images into dynamic kissing videos in just a few seconds. This quick and effortless process allows you to bring your vision to life instantly.',
      },
      {
        title: 'Make Your Favorite Anime Characters Kiss',
        description:
          'Want to see your favorite anime characters share a kiss? Our AI tool allows you to bring these iconic moments to life, turning still images into animated kissing scenes featuring beloved characters from your favorite anime series.',
      },
      {
        title: 'Create Realistic and Natural Kissing Effects',
        description:
          'Experience the magic of AI as it generates smooth, lifelike kissing animations. With advanced technology, the transitions and movements feel incredibly realistic, giving you the perfect natural kiss effect for any character or scenario.',
      },
      {
        title: 'Bring Your Characters to Life with a Kiss',
        description:
          'Transform your characters into animated lovers with a simple kiss. Our AI ensures that every kiss is seamlessly integrated, capturing authentic emotions and expressions and making the scene look both tender and genuine.',
      },
    ],
    steps: {
      title: 'How to generate AI kissing videos?',
      list: [
        {
          title: 'Select the Kiss Template',
          description: 'Click on the Kiss Template on the template page.',
        },
        {
          title: 'Upload Your Images',
          description:
            'Upload a photo of two people or two individual photos with the same aspect ratio. Ensure the image is clear and shows a front-facing, upper-body shot.',
        },
        {
          title: 'Create and Download',
          description:
            'Click Create and wait a moment for your kissing video to generate. Hover over the video and click the Download button in the bottom-right corner.',
        },
      ],
    },
    faqs: {
      title: 'FAQs About AI Kissing Generator',
      list: [
        {
          question: 'Can I create kissing videos on my phone?',
          answer:
            "Yes, Vidu's kissing generator allows you to create videos on your phone. Simply visit the Vidu template page, select the Kiss template, upload your photos, and click Create.",
        },
        {
          question: 'What format is the generated kissing video?',
          answer: 'The kissing videos are generated in MP4 format, making them compatible with most devices.',
        },
        {
          question: 'Is the kissing video generator free to use?',
          answer:
            'Yes, the Vidu kissing video generator is free to use. Vidu provides dozens of monthly credits, allowing you to create multiple kissing videos at no cost.',
        },
        {
          question: 'What is the best AI for kissing?',
          answer:
            "Vidu is one of the best AI tools for generating kissing videos because it's free and easy to use, allows customization of prompts to create the kissing videos you desire, and makes downloading simple.",
        },
      ],
    },
    generateNow: {
      title: 'AI Kissing Generator',
    },
  },
  'ai-tiktok-video-generator': {
    title: 'AI TikTok Video Generator',
    description:
      'Create viral TikTok videos with our Vidu AI TikTok video generator. Transform text or images into high-engagement videos with just one click. Choose from a variety of trending templates and generate eye-catching content!',
    features: [
      {
        title: 'Quick TikTok Video Creation',
        description:
          'Generate ready-to-post TikTok videos in under 5 minutes, freeing you to focus on content and engagement.',
      },
      {
        title: 'Appeal to More Followers',
        description:
          'Save viral characters and scenes in My Reference. Use them to create videos to increase views, likes, and follows.',
      },
      {
        title: 'One-Click TikTok Videos',
        description:
          'Create TikTok videos instantly with no editing required—upload your image and your idea to get your TikTok video.',
      },
    ],
    generates: [
      {
        title: 'Create TikTok Videos in Any Style',
        description:
          'From 2D animation to hyper-realistic visuals, cyberpunk aesthetics, and even animated cats with expressive movements—Vidu lets you create TikTok videos in any style. With advanced AI technology, Vidu ensures that characters, objects, and backgrounds remain consistent throughout your video, delivering a polished and professional look.',
      },
      {
        title: 'Generate TikTok Videos from Text & Images',
        description:
          'Transform your ideas into engaging TikTok videos with Vidu AI TikTok Video generator. Simply generate an image using AI tools, then animate them into videos using Reference to Video or Image to Video features. You can also enter text prompts only. Vidu will analyze the content to generate a TikTok video that matches your vision.',
      },
      {
        title: 'Use One-Click Viral TikTok Video Templates',
        description:
          "Skip the hassle of writing prompts and dive into video creation with Vidu's viral templates. From hugs and kisses to floating balloons and blooming flowers, these one-click templates make it easy to create trending TikTok videos. Simply upload an image, and Vidu handles the rest—perfect for beginners and non-editors.",
      },
      {
        title: 'Get Inspired by Trending AI TikTok Videos',
        description:
          'Need inspiration? Vidu curates the most creative and viral videos from our community of creators. Browse the latest trends and discover new ideas to help you craft high-engagement TikTok videos. See a video you love? Click the Recreate button to generate your version quickly and with ease!',
      },
    ],
    steps: {
      title: 'How to Make an AI-Generated TikTok Video',
      list: [
        {
          title: '1. Choose the Tool',
          description:
            'Click the Generate Now button below and select a tool you prefer—Text to Video, Image to Video, and Reference to Video.',
        },
        {
          title: '2. Upload Images or Text',
          description:
            'Upload the image(s) you want Vidu to animate or give it a description. Alternatively, input the specific description you want the AI to generate.',
        },
        {
          title: '3. Create TikTok Video',
          description:
            'Change the aspect ratio to 9:16 and make other settings if needed. Click Create to generate your TikTok videos in minutes or even seconds.',
        },
      ],
    },
    faqs: {
      title: 'FAQs about AI TikTok Video Generator',
      list: [
        {
          question: 'Is Vidu AI free to use?',
          answer:
            'We offer a free plan for our AI TikTok video generator with most features. Besides, all users will receive free credits to generate a video at no cost.',
        },
        {
          question: 'Do I need technical skills to use the Vidu AI TikTok Generator?',
          answer:
            'No technical skills required. Our platform is designed for creators of all levels, with an intuitive interface that guides you through the video creation process.',
        },
        {
          question: 'How to create viral TikTok videos?',
          answer:
            "Creating viral TikTok videos involves creativity, strategy, and understanding platform trends. Here's how you can increase your chances of going viral:\n1. Follow Trends – Use trending sounds, challenges, and effects from the FYP.\n2. Hook in 3 Seconds – Start with eye-catching visuals, suspense, or a bold question.\n3. Leverage AI Tools – Use Vidu to effortlessly generate high-quality, creative videos.\n4. Boost Engagement – Encourage comments, shares, and Duets; reply to comments with videos.\n5. Use Popular Music & Hashtags – Pick trending sounds and relevant hashtags like #FYP and #Viral.",
        },
      ],
    },
    generateNow: {
      title: 'AI TikTok Video Generator',
    },
  },
  'ai-dance-generator': {
    title: 'AI Dance Generator',
    description: 'Vidu AI Dance Generator instantly transforms static photos into dynamic, realistic dance videos.',
    features: [
      {
        title: 'One Click to Make Dance Video',
        description:
          'Transform any static image into a realistic, animated dance video with a single click—no video editing required!',
      },
      {
        title: 'Free & Easy to Use Use For All',
        description:
          'Enjoy a seamless, user-friendly experience with an AI dance generator. Free options are available, perfect for beginners and pros alike.',
      },
      {
        title: 'Advanced AI for Lifelike Motion',
        description:
          'Empowered by cutting-edge motion synthesis, Vidu AI dance generator creates realistic dance animations that stand out.',
      },
    ],
    generates: [
      {
        title: 'Create Dynamic AI Dance Video with Image',
        description:
          'With Vidu AI Image Dance Generator, you can transform a static image into a lively, professional-quality dance video that is effortless. Our advanced AI technology analyzes the image, detects key body points, and applies realistic motion mapping to generate smooth, natural dance movements. Meanwhile, Vidu AI ensures precise motion synthesis, reducing unnatural glitches and jerky animations. With that said, your videos will look seamless and engaging, perfect for all cases.',
      },
      {
        title: 'Customize Dance Movements with Flexibility',
        description:
          'Easily adjust dance movements with our AI-powered generator. Set amplitude (Auto, Small, Medium, Large) and duration (4 or 8 seconds) to personalize motion style and timing. Our AI ensures character consistency, minimizing distortion for a smooth dance experience.',
      },
      {
        title: 'Generate Dance Videos for Various Scenarios',
        description:
          'Create dance videos by bringing people to life from old photos, favorite anime figures, beloved fictional characters, and more! Whether turning vintage photos into lively dance routines or animating your favorite characters, our platform makes it easy to create dance videos for any occasion. Unleash your creativity and bring your cherished characters to the dance floor today!',
      },
      {
        title: 'Make High-Quality Outputs and Share Instantly',
        description:
          "Quality counts in video content; Vidu AI Dance Generator guarantees that your output dance videos are clear, high-quality, and social media-optimized. Your videos will be formatted for best viewing whether you're uploading on Instagram Reels, TikTok, YouTube Shorts, or another platform.",
      },
    ],
    steps: {
      title: 'How to Create a Dance Video',
      list: [
        {
          title: '1. Select Image to Video',
          description: 'Click the Generate Now button below to go to the Create page. Then, select Image to Video.',
        },
        {
          title: '2. Upload Your Image',
          description:
            'Upload a full-body photo and type "dance" or describe more details. The more specific your prompt is, the better the dance video.',
        },
        {
          title: '3. Save the Dance Video',
          description:
            'Before generation, you can tweak various settings, such as Aspect Ratio, Duration, Resolution, and more. Adjust them till you are satisfied. Then, click Create.',
        },
      ],
    },
    faqs: {
      title: 'FAQs about AI Dance Generator',
      list: [
        {
          question: 'Is Vidu AI Dance Generator free to use?',
          answer:
            'Yes! Vidu AI offers a free AI dance video generator option, allowing you to create dance videos at no cost. Free users get access to basic features, while premium plans offer higher-resolution exports, high-speed channel generation, and access to upcoming features.',
        },
        {
          question: 'Can I animate any image using Vidu AI?',
          answer:
            'Yes, you can. Vidu is more than an AI dance generator; it is also an AI image animator. You can upload clear photos of any style, and the AI will animate it with smooth movements. It works best with full-body images, ensuring natural motion mapping.',
        },
        {
          question: 'What devices and browsers are compatible with Vidu AI?',
          answer: 'Vidu AI is a web-based AI dance video generator, so no downloads are needed.',
        },
      ],
    },
    generateNow: {
      title: 'AI Dance Generator',
    },
  },
  'ai-anime-video-generator': {
    title: 'AI Anime Video Generator',
    description: 'A high-end and easy-to-use AI anime video generator empowered by cutting-edge AI from Vidu.',
    features: [
      {
        title: 'Effortless Anime Creation',
        description:
          'Our sophisticated anime video AI generator quickly turns your imagination, texts, and images into fluid-motion anime videos. No animation skills are required.',
      },
      {
        title: 'Smart AI for Vivid Scenes',
        description:
          "Vidu's AI video generator for anime uses advanced algorithms to create anime videos as you demand. Without any editing skills, you can create animations with smooth actions simply by typing.",
      },
      {
        title: 'Anime Video of Various Styles',
        description:
          'Whether you want to bring your imagination to life in the style of Ghibli or traditional Japanese animation, our anime video AI generator can do it all!',
      },
    ],
    generates: [
      {
        title: 'Boost Your Creativity with Instant Anime Video Generation',
        description:
          "What you imagine is what our AI anime video generator does. Write down your imagination, and our AI will bring it to life with the most vivid scenes and smooth motions. You can save yourself the trouble of drawing characters from scratch, editing clips, and going over various SFX just to find a suitable one. Now, with Vidu's AI, it's all streamlined into one process – type and upload!",
      },
      {
        title: 'Turn Your Text into Dynamic Anime Videos',
        description:
          "Ever imagined a whole anime scene just from a single line of dialogue or description? With Vidu's AI anime video generator, you can now make that scene real. Just type your idea and let our anime AI video generator bring it to life in motion. Whether you're writing a short story, scripting a meme, or drafting a storyboard, our AI video generator (anime style) turns your text into visuals with fluid motion.",
      },
      {
        title: 'Bring Your Images to Life with AI-Generated Animation',
        description:
          "Whether you have a drawing, a selfie, or a random screenshot, simply upload it to Vidu's anime AI generator video tool, and it instantly creates a stylized animation based on your image. The anime generation isn't just about adding effects. From characters to backgrounds, our AI-generated anime video technology analyzes your input and turns it into anime videos.",
      },
      {
        title: 'Explore Anime Video Template',
        description:
          'Create your own "animated me" with Vidu\'s anime-style template inspired by Ghibli and classic Japanese animation. Just upload a photo — of yourself, your family, or your friends — and watch it transform into a charming animated video. It\'s fun, fast, and full of personality, perfect for sharing or keeping as a creative memory.',
      },
      {
        title: 'Create Anime Videos for Any Purpose ',
        description:
          "You don't need to be an animator to do something amazing. Vidu's free AI anime video generator works for all sorts of needs: short stories, fan edits, creative projects, business pitches, or even just for fun. Our AI anime video generator from text or images gives you endless possibilities.",
      },
    ],
    steps: {
      title: "How to Use Vidu's AI Anime Video Generator",
      list: [
        {
          title: 'Input Your Prompt or Upload an Image',
          description:
            "Start by choosing whether you want to create an anime video from text or an image. Type your idea or upload a picture, and Vidu's smart AI will begin interpreting your vision in anime style. ",
        },
        {
          title: 'Select Style and Customize Video Settings',
          description:
            'If you choose the Text to Video tool in the first step, remember to pick Animation as your preferred style. Then adjust settings like resolution, video length, aspect ratio, and motion intensity to personalize your creation.',
        },
        {
          title: 'Generate Your AI Anime Video',
          description:
            'Click the Generate button and let the AI anime video generator do the rest. In just a few moments, your animated video will be ready to preview and download—no manual editing, no hassle.',
        },
      ],
    },
    faqs: {
      title: 'FAQs about AI Anime Video Generator',
      list: [
        {
          question: 'What is an AI anime video generator?',
          answer:
            "An AI anime video generator is a tool that uses smart algorithms to turn text or images into animated videos in anime style. With Vidu's anime AI video generator, you can create dynamic scenes without needing animation skills.",
        },
        {
          question: 'Can I create anime videos from just text?',
          answer:
            "Yes! With Vidu's AI anime video generator from text, you simply describe your idea, and the AI brings it to life with smooth motions and vivid anime visuals—no drawing or editing required. ",
        },
        {
          question: 'Is there a free anime AI video generator available?',
          answer:
            'Absolutely. Vidu offers a free AI anime video generator for users who want to try it out before going premium. You can generate short anime videos from text or images with no cost.',
        },
        {
          question: "What styles can I create with Vidu's anime video AI generator?",
          answer:
            "Vidu's anime video AI generator supports various styles—from Japanese anime to Disney-like cinematic animation. It's ideal for anyone wanting to experiment with creative, high-quality anime videos quickly.",
        },
      ],
    },
    generateNow: {
      title: 'AI Anime Video Generator',
    },
  },
  'ai-cartoon-video-generator': {
    title: 'AI Cartoon Video Generator',
    description:
      "Create stunning AI cartoon videos in seconds with Vidu. Vidu's AI cartoon video generator makes it easy to turn text or images into vibrant animated cartoon videos—quickly, creatively, and for free.",
    features: [
      {
        title: 'Powerful AI Engine',
        description:
          'Harness cutting-edge AI algorithms to generate high-quality cartoon videos from prompts or pictures with incredible accuracy and style.',
      },
      {
        title: 'Effortless to Use',
        description:
          "Vidu's cartoon AI video generator, designed for simplicity, helps users of any level create impressive animations in just a few clicks.",
      },
      {
        title: 'Dynamic Template Library',
        description:
          'Explore a constantly growing library of AI cartoon video templates covering a variety of themes to fit most stories or projects.',
      },
    ],
    generates: [
      {
        title: 'Instantly Catch the Eye with Unique AI Cartoon Videos',
        description:
          "Looking to make content that stands out? Vidu's AI cartoon video generator helps you create scroll-stopping videos that capture attention in seconds. Whether you're telling a story, launching a product, or sharing ideas, cartoon-style visuals make everything more fun and memorable. Just type in your prompt—or upload an image—and let the AI do the magic.",
      },
      {
        title: 'From Words to Cartoons in Just a Few Clicks',
        description:
          "Vidu makes video creation effortless. Just enter your text, choose a style, and click generate—no editing skills needed. The text to AI cartoon video generator understands what you want and delivers custom cartoon clips that feel tailor-made. It's perfect for anyone who wants quick, creative output without spending hours on design.",
      },
      {
        title: 'Turn Any Picture into a Cartoon Video',
        description:
          "Got a concept in mind? Upload an cartoon image and transform it into a smooth, cartoon video with our smart AI engine. Vidu's AI cartoon video generator frees your mind by bringing your image or static visuals to life with motion. Great for concept art, personal avatars, or social content that needs a unique, animated touch.",
      },
      {
        title: 'Explore Endless Themes with Fresh Templates',
        description:
          "Whatever vibe you're going for—funny, dramatic, educational, or inspiring—Vidu has a template for it. Before you jump to our free AI cartoon video generator, visit our constantly updated library first. The Template section offers a wide range of video themes to choose from. The AI 3D cartoon video generator adapts to your creative needs with ease.",
      },
      {
        title: 'Help Your Ideas Grow with AI-Powered Videos',
        description:
          "Cartoon videos aren't just for fun—they're powerful tools for communication. A marketer can use this AI cartoon video generator to highlight product features with a playful twist. With the text to AI cartoon video generator, your ideas get a fresh, visual edge that resonates with your audience.",
      },
    ],
    steps: {
      title: 'How to Use AI Cartoon Video Generator',
      list: [
        {
          title: 'Describe or Upload',
          description:
            "First, go to Vidu website and click the Try Vidu button. Then, you can type in what you want to see in your cartoon video or upload an image you'd like to bring to life. The smarter your input, the more tailored your cartoon video will be.",
        },
        {
          title: 'Set Your Style',
          description:
            'Pick your preferred aspect ratio, tweak the resolution, set the duration, and adjust motion levels. Vidu AI cartoon video generator gives you control so your cartoon video looks just right.',
        },
        {
          title: 'Let Vidu Work Its Magic',
          description:
            "Next, click the Create button and watch the AI turn your idea into a fully animated cartoon video. Once it's ready, you can download or share the cartoon video on social media platforms.",
        },
      ],
    },
    faqs: {
      title: 'FAQs about AI Cartoon Video Generator',
      list: [
        {
          question: 'What is an AI cartoon video generator, and how does it work?',
          answer:
            'An AI cartoon video generator like Vidu transforms text prompts or uploaded images into eye-catching cartoon videos using smart AI algorithms. You simply enter your idea or image, choose a template, and the tool generates a unique cartoon-style video in seconds—no editing skills or special software needed.',
        },
        {
          question: 'Can I create AI cartoon videos for free with Vidu?',
          answer:
            "Yes! Vidu offers an AI cartoon video generator free of charge. You can turn your text or pictures into personalized cartoon videos without paying a cent. It's perfect for individuals and small businesses who want to create engaging content without investing in expensive tools or animation services.",
        },
        {
          question: 'Who can benefit from using a cartoon AI video generator?',
          answer:
            "Vidu is great for content creators, educators, marketers, and small business owners. Whether you're using the text-to-AI cartoon video generator or uploading an image, the tool helps you communicate ideas visually in a fun, relatable way—perfect for social media posts, explainer content, or brand storytelling.",
        },
      ],
    },
    generateNow: {
      title: 'AI Cartoon Video Generator',
    },
  },
  'ai-fight-generator': {
    title: 'AI Fight Generator',
    description:
      'Create breathtaking, cinematic fight scenes instantly with the Vidu AI fight scene generator. From thrilling showdowns to anime-like duels, this powerful generator adapts to your ideas—allowing you to craft high-energy, dynamic scenes in a simple and flexible manner.',
    features: [
      {
        title: 'Hyper-Realistic Actions',
        description:
          'Generate visually stunning and realistic fight scenes with our AI fight generator. From hand-to-hand combat to energy blasts, each sequence feels dynamic and makes for high-impact reels.',
      },
      {
        title: 'Create Fight Scenes Quickly',
        description:
          'Generate professional-quality fight scenes in minutes instead of hours, saving you time and resources while maintaining high quality.',
      },
      {
        title: 'Customize Fight Video',
        description:
          "Whether you're building a solo duel or a group battle, our AI-generated fight scene tool gives you control over pacing, effects, and style to match your creative vision.",
      },
    ],
    generates: [
      {
        title: 'Use Sophisticated AI to Generate Fight Scenes',
        description:
          "Vidu's cutting-edge AI fight generator transforms simple text descriptions into stunning visual combat sequences. Meanwhile, if you choose the Image to Video tool, our proprietary algorithms will analyze your pictures and prompts to understand movement dynamics, impact physics, and realistic combat flows.",
      },
      {
        title: 'Realistic and Vivid AI-Generated Fight Scenes',
        description:
          "Experience the thrill of lifelike combat with Vidu's advanced AI technology, which generates fluid and realistic fight sequences. Each movement is crafted to capture the intensity and excitement of real-world battles, resulting in visually stunning and engaging content that captivates your audience. Start now and pour your imagination into AI-generated fights.",
      },
      {
        title: 'Seamless Anime-Style Fight Video Creation',
        description:
          "Vidu lets you bring high-energy anime battles to life with ease. By switching to the Animation style in the Text to Video tool, you can craft fast-paced, visually striking fight scenes inspired by classic anime storytelling. Whether it's a one-on-one duel or an all-out power clash, Vidu's AI anime fight generator delivers the style, speed, and intensity that anime fans love.",
      },
    ],
    steps: {
      title: 'How to Use AI Fight Generator?',
      list: [
        {
          title: 'Choose a Feature',
          description:
            'Go to Vidu and click Try Vidu button, and you can see several features. Choose from Text to Video, Image to Video, or Reference to Video.',
        },
        {
          title: 'Upload Images/Prompts',
          description:
            "If you have images for our AI as a reference and have chosen the Image to Video or Reference to Video tool, upload them now. Also, don't forget to type your prompts and describe what you envision about this fight video.",
        },
        {
          title: 'Generate a Fight Video',
          description:
            "You can adjust video duration, resolutions, and other parameters. If you'd like to upload the generated video later to TikTok, 9:16 is an ideal aspect ratio choice. After that, click Create to generate.",
        },
      ],
    },
    faqs: {
      title: 'FAQs about AI Fight Generator',
      list: [
        {
          question: 'Is it possible to generate fight scenes featuring specific styles or weapons?',
          answer:
            "Yes, it is! Vidu's AI fight scene generator allows you to generate fight scenes for almost any style – from boxing and MMA to swords and guns or even magical sword fighting! Just indicate the style you would like in your text prompt or provide some relevant reference material.",
        },
        {
          question: 'Can I create anime-style fights with Vidu AI Fight Generator?',
          answer:
            'Certainly! You can easily make anime-style fight videos using the Vidu AI anime fight generator. Just choose the "Animation" option under the Text to Video tool to switch styles. The generator will automatically add anime-style visuals and movement, to help you create a video that looks like scenes from your favorite anime series!',
        },
        {
          question: 'Do I need experience to use the AI fight scene generator?',
          answer:
            "No, you don't need much experience or skills. Vidu's AI fight scene generator is designed for users of all levels. With multiple-generation tools and ready-made templates, you can create high-quality, AI-generated fight scenes in just a few clicks.",
        },
      ],
    },
    generateNow: {
      title: 'AI Fight Generator',
    },
  },
  'ai-meme-generator': {
    title: 'AI Meme Video Generator',
    description:
      "Vidu's AI video meme generator makes it effortless to turn memes into viral-worthy meme videos. Try our free meme video generator online and get your content trending fast.",
    features: [
      {
        title: 'Easy Meme Video Creation',
        description:
          'Turn any meme into a meme video in seconds using our AI meme video generator—no editing skills or software downloads required.',
      },
      {
        title: 'AI-Driven Precision',
        description:
          "Let smart algorithms do the work. Vidu's AI video meme generator automatically animates your uploaded photos/memes to do as what your prompt describes.",
      },
      {
        title: 'Free & Online Access',
        description:
          'Use our video meme generator online anytime, anywhere. No fees, no installs—just fast, free meme video creation at your fingertips.',
      },
    ],
    generates: [
      {
        title: 'Smart AI Video Meme Generator for Fast Viral Content',
        description:
          "Vidu's AI video meme generator transforms your ideas, images, or references into videos fast. Instead of manual editing, Vidu uses intelligent AI to generate meme videos from meme with simple text prompts. This approach saves creators up to 80% of the time they'd normally spend on video editing.",
      },
      {
        title: 'Flexible Ways to Generate Viral Meme Videos',
        description:
          "Vidu's AI meme video generator gives you complete creative freedom by supporting multiple input types. Whether you start with a single image, write a text prompt, or use a reference image, Vidu's AI brings your vision to life with dynamic, high-quality output. Unleash your creativity now because everything you've imagined can be animated in Vidu.",
      },
      {
        title: 'More Views and Engagement with Effortless Meme Videos',
        description:
          "In today's content-saturated world, meme videos are one of the fastest ways to stand out and grow your online presence. Vidu's AI meme video generator helps creators and influencers produce eye-catching content without the need for expensive software or installation. Turn your daily thoughts and trending memes into short videos with Vidu's smart meme video generator now!",
      },
    ],
    steps: {
      title: 'How to Make a Meme Video',
      list: [
        {
          title: 'Choose the Tool You Need',
          description: 'First, click Generate Now and choose from Image to Video or Reference to Video.',
        },
        {
          title: 'Upload Pictures or Input Texts',
          description: 'Now, you can upload a meme picture and describe the result you want to achieve.',
        },
        {
          title: 'Adjust Settings and Generate',
          description:
            'Set your video duration, resolution, aspect ratios and more. Next, click the Create button to generate a meme video.',
        },
      ],
    },
    faqs: {
      title: 'FAQs about AI Meme Video Generator',
      list: [
        {
          question: 'What is a video meme generator and how does Vidu work?',
          answer:
            "A video meme generator is a tool that turns memes into meme-style video content. Vidu uses advanced AI to transform your input—whether it's a text prompt, image, or reference video—into attention-grabbing meme videos. It's fast, browser-based, and perfect for creators looking to streamline their content creation without learning complex editing tools.",
        },
        {
          question: 'Can I use the Vidu AI meme video generator for free?',
          answer:
            "Yes! Vidu offers free access to its AI meme video generator online. You can start creating meme videos with just a few clicks—no payment or installation required. It's a great way for creators to experiment with content and see how AI can enhance their social media presence before upgrading to advanced features.",
        },
        {
          question: 'How can meme video generators help grow my social media following?',
          answer:
            'First of all, you can spend less energy but still stay on top of trends. Why? Because, a smart meme video generator like Vidu helps you do most of the work and editing. Also, you can post faster, and create highly shareable content. With Vidu, you can produce engaging videos regularly, which is key to growing and maintaining a strong follower base.',
        },
      ],
    },
    generateNow: {
      title: 'AI Meme Video Generator',
    },
  },
  'ai-reel-generator': {
    title: 'AI Reel Generator',
    description:
      "Turn your ideas into captivating IG reels instantly with Vidu's AI Reel Generator. Whether you're an influencer seeking engagement or a brand-establishing presence, our smart video-creating tool turns text, images, and references into eye-catching content that stands out on cluttered feeds.",
    features: [
      {
        title: 'Easy Reel Creation',
        description:
          'Save hours of editing time with our intuitive interface that turns your concepts into professionally styled reels in just minutes.',
      },
      {
        title: 'Multiple Creation Modes',
        description:
          'Create Reels your way—use text-to-video, image-to-video, or reference-to-video methods. Perfect for every creative need.',
      },
      {
        title: 'Various Templates',
        description:
          'Select from various and diverse Reel templates professionally designed to match different audience preferences.',
      },
    ],
    generates: [
      {
        title: 'AI Reel Generator with Text that Visualizes Your Wildest Ideas',
        description:
          'Our advanced AI text-to-video engine turns your written concepts into compelling reels that capture attention. Simply input your script or content idea, and watch as our intelligent AI Instagram Reel generator creates a professionally styled video complete with relevant visuals, timing, and transitions.',
      },
      {
        title: 'Convenient AI Reels Generator from Images',
        description:
          "Simply upload your product photos, artwork, or event pictures, and Vidu's AI Instagram Reel generator will create dynamic Reels using intelligent motion graphics and transitions. It's the perfect tool for small business owners or creators who depend on visual storytelling. Save on video editing costs while gaining more views with AI-generated Reels.",
      },
      {
        title: 'Keep Up with the Reels Trend with Various Templates',
        description:
          "Stay on top of the latest Reels trends with Vidu's wide variety of animation templates! Whether it's a sweet Kiss, a warm Hug, or playful themes like Balloon, Fly Me, Animated Me, or Nap Me, Vidu makes it easy to bring your characters to life in just a few clicks. Perfect for adding a personal, dynamic touch to your short videos and social posts!",
      },
      {
        title: 'Versatile Use Cases for Every Content Creator',
        description:
          "Vidu's AI Reel Generator delivers amazing outcomes in a range of situations, making it a one-stop solution for all your content requirements. Instagram influencers use our service to post on timelines regularly and boost production quality—getting a 41% boost in gaining followers. In the short term, Vidu's smart AI Reel Generator can be a real boost for your Instagram Reel video view.",
      },
    ],
    steps: {
      title: 'How to Use AI to Generate Reels for Instagram',
      list: [
        {
          title: 'Choose a Preferred Tool',
          description:
            'Go to Vidu and choose from Text to Video, Image to Video, and Reference to Video. You can also browse our templates for more brilliant ideas for your Reels.',
        },
        {
          title: 'Upload Images or Prompts',
          description:
            'If you have a picture, upload it. But if you chose Text to Video in the previous step, then type your prompt to describe the Reel video.',
        },
        {
          title: 'Generate Reels with AI',
          description:
            'Adjust a few settings to your preferred value. These include video resolution, duration, aspect ratio, and more. For Instagram Reels, 9:16 is a popular aspect ratio. Click Create to generate.',
        },
      ],
    },
    faqs: {
      title: 'FAQs about AI Reel Generator',
      list: [
        {
          question: 'What platforms are compatible with reels created through Vidu?',
          answer:
            "Videos generated through Vidu AI Reel Generator are optimized for all major social platforms, including Instagram, TikTok, YouTube Shorts, and Facebook Reels. You can always manually adjust aspect ratios, durations, and technical specifications to meet each platform's requirements.",
        },
        {
          question: 'Can I create Reels using just text input?',
          answer:
            "Yes! Vidu's AI Reel Generator allows you to input plain text and instantly turns it into a visually compelling Reel. This text-to-video tool is perfect for script-based or quote-driven content.",
        },
        {
          question: 'Is there a way to use my existing photos in a Reel?',
          answer:
            'Absolutely. The image-to-video feature enables users to upload images and convert them into dynamic Reels with smooth transitions and vivid frames.',
        },
      ],
    },
    generateNow: {
      title: 'AI Reel Generator',
    },
  },
};

export const SectionGradient: Record<number, string> = {
  0: 'bg-[linear-gradient(270deg,#C4D9FF_0%,#DBF5FF_79.5%)]',
  2: 'bg-[linear-gradient(270deg,#F3EFFF_20.5%,#E6DDFF_100%)]',
  4: 'bg-[linear-gradient(270deg,#C4D9FF_0%,#DBF5FF_79.5%)]',
};

export const FeatureGradient: Record<number, string> = {
  0: 'bg-[linear-gradient(145deg,#FEB890_14.28%,#FE47AC_92.03%)]',
  1: 'bg-[linear-gradient(145deg,#71DFFF_14.28%,#467DFF_92.03%)]',
  2: 'bg-[linear-gradient(145deg,#D4A7FF_14.28%,#7066FF_92.03%)]',
};
